/**
 * AP API service — T17 — Payment Runs, Vendor Payment Methods, AP Reports,
 * 1099 Center, Bill duplicate detection.
 */
import {
  apiGet,
  apiPost,
  apiPut,
  apiDelete,
  getValidAccessToken,
} from '../../../shared/api/axiosInstance';
import { API_BASE_URL } from '../../../shared/api/config';
import type { DataResponse, PaginatedResponse } from '../types';
import type {
  PaymentRun,
  PaymentReversal,
  PaymentReversalType,
  AchReturnCode,
  PaymentRunBillRow,
  PaymentRunCreate,
  PaymentRunUpdate,
  SelectableBill,
  VendorPaymentMethod,
  VendorPaymentMethodCreate,
  VendorPaymentMethodUpdate,
  WireInstruction,
  ApAgingReport,
  GrniAgingReport,
  CashRequirementsReport,
  Form1099SummaryResponse,
  FormFiling,
  DuplicateCheckResponse,
  PaymentsDueResponse,
} from '../types/ap';

const API_BASE = '/api/v1/accounting';

// `unknown` would force consumer-side narrowing; the call sites pass
// well-typed param interfaces. The function itself coerces every value to
// string via URLSearchParams, so accepting `any` here is contained.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildParams(params?: Record<string, any>): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value)) {
      for (const v of value) sp.append(key, String(v));
    } else {
      sp.set(key, String(value));
    }
  }
  const str = sp.toString();
  return str ? `?${str}` : '';
}

export interface ListPaymentRunsParams {
  status?: string;
  company_id?: string;
  rail?: string;
  page?: number;
  page_size?: number;
}

export type PaymentRunDetail = PaymentRun & {
  run_bills?: PaymentRunBillRow[];
};

export interface AddBillsRequest {
  bills: AddBillEntry[];
}

export interface MarkDeliveredRequest {
  delivery_method: 'manual_download' | 'sftp' | 'bank_api';
  delivery_reference?: string;
}

export interface ApproveRequest {
  notes?: string;
}

export interface MarkDeliveredRequest {
  delivered_at?: string;
}

export interface VoidRequest {
  reason: string;
}

export interface RecallRequest {
  bank_reference: string;
  reason: string;
}

export interface ConfirmRecallRequest {
  reversal_date?: string;
  reason?: string;
}

export interface RecordReversalRequest {
  reversal_type: PaymentReversalType;
  reversal_date: string;
  reason: string;
  bank_reference?: string;
  r_code?: string;
  wire_return_reference?: string;
}

// No `company_id`: the backend scopes the preview to the run's own company, read off
// the run identified by `run_id`. Sending one had no effect but to steer the query.
export interface SelectBillsParams {
  run_id: string;
  due_date_before?: string;
  vendor_ids?: string[];
  only_with_discount?: boolean;
}

export interface AddBillEntry {
  bill_id: string;
  amount_to_pay: number;
  discount_taken?: number;
  vendor_payment_method_id?: string;
}

// ============== Payment Runs ==============

export const paymentRunApi = {
  list: async (
    params?: ListPaymentRunsParams,
  ): Promise<PaginatedResponse<PaymentRun>> =>
    apiGet<PaginatedResponse<PaymentRun>>(
      `${API_BASE}/payment-runs${buildParams(params)}`,
    ),

  get: async (id: string): Promise<DataResponse<PaymentRunDetail>> =>
    apiGet<DataResponse<PaymentRunDetail>>(`${API_BASE}/payment-runs/${id}`),

  create: async (
    data: PaymentRunCreate,
  ): Promise<DataResponse<PaymentRun>> =>
    apiPost<DataResponse<PaymentRun>>(`${API_BASE}/payment-runs`, data),

  update: async (
    id: string,
    data: PaymentRunUpdate,
  ): Promise<DataResponse<PaymentRun>> =>
    apiPut<DataResponse<PaymentRun>>(`${API_BASE}/payment-runs/${id}`, data),

  selectBills: async (
    params: SelectBillsParams,
  ): Promise<DataResponse<SelectableBill[]>> => {
    const { run_id, ...body } = params;
    return apiPost<DataResponse<SelectableBill[]>>(
      `${API_BASE}/payment-runs/${run_id}/select-bills`,
      body,
    );
  },

  addBills: async (
    id: string,
    bills: AddBillEntry[],
  ): Promise<DataResponse<PaymentRun>> =>
    apiPost<DataResponse<PaymentRun>>(
      `${API_BASE}/payment-runs/${id}/bills`,
      { bills } as AddBillsRequest,
    ),

  removeBill: async (id: string, billId: string): Promise<void> => {
    await apiDelete(`${API_BASE}/payment-runs/${id}/bills/${billId}`);
  },

  submit: async (id: string): Promise<DataResponse<PaymentRun>> =>
    apiPost<DataResponse<PaymentRun>>(`${API_BASE}/payment-runs/${id}/submit`),

  approve: async (
    id: string,
    notes?: string,
  ): Promise<DataResponse<PaymentRun>> =>
    apiPost<DataResponse<PaymentRun>>(
      `${API_BASE}/payment-runs/${id}/approve`,
      { notes },
    ),

  markDelivered: async (
    id: string,
    data: MarkDeliveredRequest = { delivery_method: 'manual_download' },
  ): Promise<DataResponse<PaymentRun>> =>
    apiPost<DataResponse<PaymentRun>>(
      `${API_BASE}/payment-runs/${id}/mark-delivered`,
      data,
    ),

  complete: async (id: string): Promise<DataResponse<PaymentRun>> =>
    apiPost<DataResponse<PaymentRun>>(
      `${API_BASE}/payment-runs/${id}/complete`,
    ),

  void: async (
    id: string,
    reason: string,
  ): Promise<DataResponse<PaymentRun>> =>
    apiPost<DataResponse<PaymentRun>>(`${API_BASE}/payment-runs/${id}/void`, {
      reason,
    } as VoidRequest),

  stopPayment: async (
    id: string,
    reason: string,
  ): Promise<DataResponse<PaymentRun>> =>
    apiPost<DataResponse<PaymentRun>>(
      `${API_BASE}/payment-runs/${id}/stop-payment`,
      { reason },
    ),

  recall: async (
    id: string,
    data: RecallRequest,
  ): Promise<DataResponse<PaymentRun>> =>
    apiPost<DataResponse<PaymentRun>>(
      `${API_BASE}/payment-runs/${id}/recall`,
      data,
    ),

  confirmRecall: async (
    id: string,
    data: ConfirmRecallRequest = {},
  ): Promise<DataResponse<PaymentRun>> =>
    apiPost<DataResponse<PaymentRun>>(
      `${API_BASE}/payment-runs/${id}/recall/confirm`,
      data,
    ),

  rejectRecall: async (
    id: string,
    reason: string,
  ): Promise<DataResponse<PaymentRun>> =>
    apiPost<DataResponse<PaymentRun>>(
      `${API_BASE}/payment-runs/${id}/recall/reject`,
      { reason },
    ),

  recordReversal: async (
    id: string,
    data: RecordReversalRequest,
  ): Promise<DataResponse<PaymentRun>> =>
    apiPost<DataResponse<PaymentRun>>(
      `${API_BASE}/payment-runs/${id}/record-reversal`,
      data,
    ),

  listReversals: async (
    id: string,
  ): Promise<DataResponse<PaymentReversal[]>> =>
    apiGet<DataResponse<PaymentReversal[]>>(
      `${API_BASE}/payment-runs/${id}/reversals`,
    ),

  achReturnCodes: async (): Promise<DataResponse<AchReturnCode[]>> =>
    apiGet<DataResponse<AchReturnCode[]>>(
      `${API_BASE}/payment-runs/meta/ach-return-codes`,
    ),

  downloadFile: async (id: string): Promise<Blob> => {
    const token = await getValidAccessToken();
    const res = await fetch(
      `${API_BASE_URL}${API_BASE}/payment-runs/${id}/file`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (!res.ok) throw new Error(`Failed to download file: ${res.statusText}`);
    return res.blob();
  },

  getRemittance: async (
    runId: string,
    paymentId: string,
  ): Promise<DataResponse<{ html: string; text: string }>> =>
    apiGet<DataResponse<{ html: string; text: string }>>(
      `${API_BASE}/payment-runs/${runId}/remittance-advice/${paymentId}`,
    ),
};

// ============== Vendor Payment Methods ==============

export const vendorPaymentMethodApi = {
  list: async (
    vendorId: string,
  ): Promise<DataResponse<VendorPaymentMethod[]>> =>
    apiGet<DataResponse<VendorPaymentMethod[]>>(
      `${API_BASE}/vendors/${vendorId}/payment-methods`,
    ),

  create: async (
    vendorId: string,
    data: VendorPaymentMethodCreate,
  ): Promise<DataResponse<VendorPaymentMethod>> =>
    apiPost<DataResponse<VendorPaymentMethod>>(
      `${API_BASE}/vendors/${vendorId}/payment-methods`,
      data,
    ),

  update: async (
    vendorId: string,
    methodId: string,
    data: VendorPaymentMethodUpdate,
  ): Promise<DataResponse<VendorPaymentMethod>> =>
    apiPut<DataResponse<VendorPaymentMethod>>(
      `${API_BASE}/vendors/${vendorId}/payment-methods/${methodId}`,
      data,
    ),

  delete: async (vendorId: string, methodId: string): Promise<void> => {
    await apiDelete(
      `${API_BASE}/vendors/${vendorId}/payment-methods/${methodId}`,
    );
  },

  setDefault: async (
    vendorId: string,
    methodId: string,
  ): Promise<DataResponse<VendorPaymentMethod>> =>
    apiPost<DataResponse<VendorPaymentMethod>>(
      `${API_BASE}/vendors/${vendorId}/payment-methods/${methodId}/set-default`,
    ),

  // Builds the beneficiary wire/ACH instruction. Returns the decrypted full
  // account number, so this is fetched imperatively on demand and never cached
  // to the offline store.
  wireInstruction: async (
    vendorId: string,
    methodId: string,
    params: { amount?: number; currency_code?: string; reference?: string } = {},
  ): Promise<DataResponse<WireInstruction>> => {
    const qs = new URLSearchParams();
    if (params.amount != null) qs.set('amount', String(params.amount));
    if (params.currency_code) qs.set('currency_code', params.currency_code);
    if (params.reference) qs.set('reference', params.reference);
    const query = qs.toString();
    return apiGet<DataResponse<WireInstruction>>(
      `${API_BASE}/vendors/${vendorId}/payment-methods/${methodId}/wire-instruction${
        query ? `?${query}` : ''
      }`,
    );
  },
};

// ============== AP Reports ==============

export interface ApAgingParams {
  company_id?: string;
  vendor_id?: string;
  as_of?: string;
  currency_code?: string;
  include_zero_balance_vendors?: boolean;
}

export interface GrniAgingParams {
  company_id?: string;
  vendor_id?: string;
  as_of?: string;
  currency_code?: string;
}

export interface CashRequirementsParams {
  company_id?: string;
  horizon_days?: number;
  as_of?: string;
  currency_code?: string;
  only_with_discount?: boolean;
}

export interface Form1099Params {
  company_id?: string;
  currency_code?: string;
}

export interface Generate1099Request {
  company_id: string;
  include_below_minimum?: boolean;
  currency_code?: string;
}

export const apReportsApi = {
  apAging: async (
    params?: ApAgingParams,
  ): Promise<DataResponse<ApAgingReport>> =>
    apiGet<DataResponse<ApAgingReport>>(
      `${API_BASE}/reports/ap-aging${buildParams(params)}`,
    ),

  grniAging: async (
    params?: GrniAgingParams,
  ): Promise<DataResponse<GrniAgingReport>> =>
    apiGet<DataResponse<GrniAgingReport>>(
      `${API_BASE}/reports/grni-aging${buildParams(params)}`,
    ),

  cashRequirements: async (
    params?: CashRequirementsParams,
  ): Promise<DataResponse<CashRequirementsReport>> =>
    apiGet<DataResponse<CashRequirementsReport>>(
      `${API_BASE}/reports/cash-requirements${buildParams(params)}`,
    ),

  get1099: async (
    taxYear: number,
    params?: Form1099Params,
  ): Promise<DataResponse<Form1099SummaryResponse>> =>
    apiGet<DataResponse<Form1099SummaryResponse>>(
      `${API_BASE}/reports/1099/${taxYear}${buildParams(params)}`,
    ),

  generate1099: async (
    taxYear: number,
    data: Generate1099Request,
  ): Promise<DataResponse<FormFiling[]>> =>
    apiPost<DataResponse<FormFiling[]>>(
      `${API_BASE}/reports/1099/${taxYear}/generate`,
      data,
    ),

  generate1099MagneticFile: async (
    taxYear: number,
    data: Generate1099Request,
  ): Promise<DataResponse<FormFiling>> =>
    apiPost<DataResponse<FormFiling>>(
      `${API_BASE}/reports/1099/${taxYear}/magnetic-file`,
      data,
    ),
};

// ============== Payments Due (consolidated AP worklist) ==============

export interface PaymentsDueParams {
  company_id?: string;
  horizon_days?: number;
  source_type?: 'bill' | 'deposit';
  overdue_only?: boolean;
  offset?: number;
  limit?: number;
}

export const payablesApi = {
  paymentsDue: async (
    params?: PaymentsDueParams,
  ): Promise<PaymentsDueResponse> =>
    apiGet<PaymentsDueResponse>(
      `${API_BASE}/payables/payments-due${buildParams(params)}`,
    ),
};

// ============== Payments-Due bank-file export ==============

export type BankFileFormat =
  | 'nacha'
  | 'iso20022_pain001'
  | 'swift_mt103'
  | 'bacs_std18';

export interface ExportAvailableMethod {
  id: string;
  label: string;
  last4: string | null;
}

export interface ExportPreviewLine {
  bill_id: string;
  bill_number: string | null;
  vendor_id: string | null;
  vendor_name: string | null;
  amount_due: number;
  currency_code: string;
  method_id: string | null;
  method_label: string | null;
  method_last4: string | null;
  available_methods: ExportAvailableMethod[];
  exportable: boolean;
  issues: string[];
}

export interface ExportPreviewResponse {
  lines: ExportPreviewLine[];
  funding_account_id: string;
  funding_issues: string[];
  exportable_count: number;
  total_amount: number;
  currency_codes: string[];
}

export interface ExportDefaults {
  file_format: BankFileFormat | null;
  payment_method_code: string | null;
  funding_account_id: string | null;
}

export interface ExportPreviewBody {
  company_id: string;
  bill_ids: string[];
  file_format: BankFileFormat;
  payment_method_code: string;
  funding_account_id: string;
  method_overrides?: Record<string, string>;
}

export interface ExportFileBody extends ExportPreviewBody {
  effective_date?: string;
}

export interface MarkPaidBody {
  company_id: string;
  bill_ids: string[];
  funding_account_id: string;
  payment_method_code: string;
  payment_date?: string;
}

export interface MarkPaidResponse {
  recorded: number;
  // Bills the server stopped short of recording when its per-request time budget
  // elapsed (keeps each request under the gateway window). Empty when the whole
  // selection was processed; otherwise the caller re-submits these to continue.
  remaining_bill_ids: string[];
}

export const payablesExportApi = {
  getDefaults: async (): Promise<ExportDefaults> =>
    apiGet<ExportDefaults>(`${API_BASE}/payables/export/defaults`),

  preview: async (body: ExportPreviewBody): Promise<ExportPreviewResponse> =>
    apiPost<ExportPreviewResponse>(`${API_BASE}/payables/export/preview`, body),

  // Streams a file attachment — fetch directly so we get the Blob + filename.
  exportFile: async (
    body: ExportFileBody,
  ): Promise<{ blob: Blob; filename: string }> => {
    const token = await getValidAccessToken();
    const res = await fetch(`${API_BASE_URL}${API_BASE}/payables/export`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const detail = await res.json().catch(() => null);
      throw new Error(detail?.detail?.message || `Export failed: ${res.statusText}`);
    }
    const disposition = res.headers.get('Content-Disposition') || '';
    const match = disposition.match(/filename="?([^"]+)"?/);
    const filename = match ? match[1] : 'bank-file.txt';
    return { blob: await res.blob(), filename };
  },

  markPaid: async (body: MarkPaidBody): Promise<MarkPaidResponse> =>
    apiPost<MarkPaidResponse>(`${API_BASE}/payables/export/mark-paid`, body),
};

// ============== Bill Duplicate Check ==============

export interface DuplicateCheckParams {
  vendor_id?: string;
  vendor_invoice_number?: string;
  total: number;
  issue_date?: string;
  exclude_bill_id?: string;
}

export const billDuplicateCheckApi = {
  check: async (
    params: DuplicateCheckParams,
  ): Promise<DataResponse<DuplicateCheckResponse>> =>
    apiGet<DataResponse<DuplicateCheckResponse>>(
      `${API_BASE}/bills/duplicate-check${buildParams(params)}`,
    ),
};
